import React, { useState, useCallback } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import moment from 'moment';

import { CheckboxSlider } from 'ui-components/components/checkbox-slider';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { InfoIcon } from 'ui-components/components/info-icon';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { Text, TextType } from 'ui-components/components/text';

import { getTotalMinutes, isMidnight } from '../../../../services/datetime-service';

import {
  twelveHourUserSetting,
  twelveHourUserSettingsNoTimeZoneUnpadded,
  twentyFourHourTimeUnpadded,
  twelveHourPrefix,
} from '../../../../constants/timezone-format';

import style from './time-picker.module.less';

const messages = defineMessages({
  includeTimeLabel: {
    defaultMessage: 'Include time {icon}',
    description: 'label for includes time toggle',
    id: 'MG+c5f',
  },
  tooltipText: {
    defaultMessage: 'The date and time are displayed according to your ShipStation timezone setting and may differ from other systems.',
    description: 'label for includes time toggle',
    id: 'ID8Gqk',
  },
  timeDropdownLabel: {
    defaultMessage: 'Time',
    description: 'label for time dropdown',
    id: 'd5oHt1',
  },
});

const TOTAL_HALF_HOUR_INTERVALS = 48;
const MINUTE_INTERVALS = Array.from({ length: TOTAL_HALF_HOUR_INTERVALS }, (_, i) => i * 30);

const generateTimesWithMoment = (interval, timeFormat) => {
  const time = moment().startOf('day');
  time.add(interval, 'minutes');
  return time.format(timeFormat);
};

const TimeToggleLabel = () => {
  const icon = <InfoIcon color={THEME_CONSTANTS.unstyled} size='small' tooltipText={<FormattedMessage {...messages.tooltipText} />} />;
  return <FormattedMessage {...messages.includeTimeLabel} values={{ icon }} />;
};

/**
 * TimePicker component
 * @param {object} props
 * @param {(time: moment.Moment) => void} props.onChange - function to call when the date time is changed
 * @param {moment.Moment | undefined} props.datetime - the datetime to display
 * @param {string} [props.timeFormat] - the format to display the time in
 * @returns {React.ReactElement} - the time picker component
 */
export const TimePicker = ({ onChange, datetime, timeFormat = twelveHourUserSetting }) => {
  const shouldToggleBeOn = datetime ? !isMidnight(datetime) : false;

  const [includesTime, setIncludesTime] = useState(shouldToggleBeOn);

  const is12Hour = timeFormat.startsWith(twelveHourPrefix);
  const normalizedTimeFormat = is12Hour ? twelveHourUserSettingsNoTimeZoneUnpadded : twentyFourHourTimeUnpadded;
  const time = datetime ? datetime.format(normalizedTimeFormat) : undefined;
  const totalMinutes = datetime ? getTotalMinutes(datetime) : 0;
  const shouldInjectTimeIntoDropdown = totalMinutes % 30 !== 0;

  // handlers
  const handleTimeChange = useCallback(
    (timeInterval) => {
      // deep copy the datetime to avoid mutating the original prop
      const updatedDatetime = datetime.clone();
      updatedDatetime.startOf('day');
      const hoursToAdd = Math.floor(timeInterval / 60);
      const minutesToAdd = timeInterval % 60;
      updatedDatetime.add(hoursToAdd, 'hours');
      updatedDatetime.add(minutesToAdd, 'minutes');
      onChange(updatedDatetime);
    },
    [datetime, onChange],
  );

  const resetTimeToMidnight = useCallback(() => {
    // deep copy the datetime to avoid mutating the original prop
    const updatedDatetime = datetime.clone();
    updatedDatetime.startOf('day');
    onChange(updatedDatetime);
  }, [datetime, onChange]);

  const handleToggle = useCallback(() => {
    setIncludesTime((prev) => !prev);
    if (includesTime) {
      resetTimeToMidnight();
    }
  }, [includesTime, resetTimeToMidnight]);

  return (
    <div className={style['time-picker-container']}>
      <div className={style['time-picker-contents']}>
        <CheckboxSlider
          checkboxId='includes-time-toggle'
          checked={includesTime}
          className={style['checkbox-slider']}
          disabled={!datetime}
          labelText={<TimeToggleLabel />}
          onChange={handleToggle}
        />
        {includesTime && (
          <>
            <Text textType={TextType.Label} value={{ ...messages.timeDropdownLabel }} />
            <DropdownMenu className={style['time-picker-dropdown']} dropdownMenuShouldHaveWidthOfButton variant='form'>
              <DropdownMenuToggler className={style['time-picker-dropdown-toggler']}>{time}</DropdownMenuToggler>
              {shouldInjectTimeIntoDropdown && (
                <DropdownMenuItem onClick={() => handleTimeChange(totalMinutes)} selected>
                  {generateTimesWithMoment(totalMinutes, normalizedTimeFormat)}
                </DropdownMenuItem>
              )}
              {MINUTE_INTERVALS.map((minutes) => (
                <DropdownMenuItem key={minutes} onClick={() => handleTimeChange(minutes)} selected={totalMinutes === minutes}>
                  {generateTimesWithMoment(minutes, normalizedTimeFormat)}
                </DropdownMenuItem>
              ))}
            </DropdownMenu>
          </>
        )}
      </div>
    </div>
  );
};
